<template>
    <div>
        <div class="w-11/12 mx-auto mt-4 h-full">
            <div class="bg-white rounded-lg shadow-md px-10 py-4 mx-auto mt-4 overflow-y-scroll h-[80vh]">
                <div v-for="(question, index) in questions" :key="question.question_id" class="mb-3 py-4 border-b">
                    <div class="mb-6 flex justify-between items-center">
                        <p class="font-bold text-xl text-gray-800">Question {{ index + 1 }}/{{ questions.length }}</p>
                        <div class="flex justify-end">
                            <button v-if="!question.isEditing" @click="startEditing(question)"
                                class="text-orange-600 hover:text-orange-700">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                                    stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>Edit
                            </button>
                        </div>
                    </div>

                    <div v-if="!question.isEditing" class="mb-6">
                        <p>{{ question.question }}</p>
                    </div>

                    <!-- Editing Mode -->
                    <div v-else class="mb-6">
                        <textarea v-model="question.editedQuestion" class="w-full border rounded p-2 mb-4"
                            placeholder="Edit question text" rows="3"></textarea>
                    </div>

                    <!-- Options Display/Edit -->
                    <div class="space-y-4">
                        <div v-for="optionKey in ['option_1', 'option_2', 'option_3', 'option_4']" :key="optionKey">
                            <label v-if="!question.isEditing" class="flex items-center cursor-pointer" :class="{
                                'bg-green-200': question[optionKey] === question.answer,
                                'bg-red-200':
                                    question[optionKey] === question.selected_answer &&
                                    question.selected_answer !== question.answer,
                            }" style="padding: 0.5rem; border-radius: 0.25rem;">
                                <input type="radio" :name="'question-' + question.question_id"
                                    :value="question[optionKey]"
                                    :checked="question.selected_answer === question[optionKey]" class="mr-2" disabled />
                                {{ question[optionKey] }}
                            </label>

                            <!-- Editing Options -->
                            <div v-else class="flex items-center mb-2">
                                <input type="radio" :name="'edit-' + question.question_id"
                                    v-model="question.editedAnswer"
                                    :value="question['edited' + optionKey.charAt(0).toUpperCase() + optionKey.slice(1)]"
                                    class="mr-2" />
                                <input
                                    v-model="question['edited' + optionKey.charAt(0).toUpperCase() + optionKey.slice(1)]"
                                    class="flex-grow border rounded p-2"
                                    :placeholder="`Edit ${optionKey.replace('_', ' ')}`" />
                            </div>
                        </div>
                    </div>

                    <!-- Edit Mode Action Buttons -->
                    <div v-if="question.isEditing" class="flex justify-end mt-4 space-x-2">
                        <button @click="cancelEdit(question)"
                            class="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600">
                            Cancel
                        </button>
                        <button @click="saveEdit(question)"
                            class="bg-custom-yellow text-white px-4 py-2 rounded hover:bg-orange-500">
                            Save
                        </button>
                    </div>
                </div>
            </div>
            <div class="flex justify-end mt-6">
                <button class="bg-custom-yellow hover:bg-orange-600 text-white py-2 px-4 rounded"
                    @click="handleBackToAssessmentPage">
                    Back
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/services/api';
import SweetAlertService from '@/services/SweetAlertService';

export default {
    name: 'QuestionsReviewPage',
    data() {
        return {
            questions: [],
            assessment_id: null,
        };
    },
    created() {
        this.assessment_id = this.$route.params.assessment_id;
        this.fetchQuestions();
    },
    methods: {
        async fetchQuestions() {
            try {
                const response = await api.getQuestions(this.assessment_id);
                this.questions = (response.data || response).map(q => ({
                    ...q,
                    isEditing: false,
                    editedQuestion: q.question,
                    editedOption_1: q.option_1,
                    editedOption_2: q.option_2,
                    editedOption_3: q.option_3,
                    editedOption_4: q.option_4,
                    editedAnswer: q.answer,
                }));
            } catch (error) {
                console.error('Failed to fetch questions:', error.message || error);
                await SweetAlertService.showError('Error', 'Failed to load questions');
            }
        },
        startEditing(question) {
            // Reset edited fields to current values
            question.editedQuestion = question.question;
            question.editedOption_1 = question.option_1;
            question.editedOption_2 = question.option_2;
            question.editedOption_3 = question.option_3;
            question.editedOption_4 = question.option_4;
            question.editedAnswer = question.answer;
            question.isEditing = true;
        },
        cancelEdit(question) {
            question.isEditing = false;
        },
        async saveEdit(question) {
            // Validate the edited question
            if (
                !question.editedQuestion ||
                !question.editedOption_1 ||
                !question.editedOption_2 ||
                !question.editedOption_3 ||
                !question.editedOption_4 ||
                !question.editedAnswer
            ) {
                await SweetAlertService.showError(
                    'Error',
                    'Please fill out the question, all options, and select an answer.',
                );
                return;
            }

            try {
                // Prepare payload for a single question
                const payload = {
                    questions: [
                        {
                            question_id: question.question_id,
                            question: question.editedQuestion,
                            option_1: question.editedOption_1,
                            option_2: question.editedOption_2,
                            option_3: question.editedOption_3,
                            option_4: question.editedOption_4,
                            answer: question.editedAnswer,
                        },
                    ],
                };

                // Send update request
                await api.updateQuestion(payload);

                // Update local state
                question.question = question.editedQuestion;
                question.option_1 = question.editedOption_1;
                question.option_2 = question.editedOption_2;
                question.option_3 = question.editedOption_3;
                question.option_4 = question.editedOption_4;
                question.answer = question.editedAnswer;
                question.isEditing = false;

                await SweetAlertService.showSuccess('Success', 'Question updated successfully');
            } catch (error) {
                console.error('Failed to update question:', error.message || error);
                const errorMessage =
                    error.response?.data?.errors?.join(', ') ||
                    error.message ||
                    'Failed to update question';
                await SweetAlertService.showError('Error', errorMessage);
            }
        },
        handleBackToAssessmentPage() {
            this.$router.push({
                name: 'SupervisorAssessementDetailsPage',
                params: { assessment_id: this.assessment_id },
            });
        },
    },
};
</script>

<style scoped>
.overflow-y-scroll {
    overflow-y: scroll;
}
</style>